import { useEffect, useRef } from 'react'
import { DefaultDurations } from '../../../shared/lib/blockRanges'
import { useDispositionDraftStore } from './useDispositionDraftStore'
import { ResizeHandle } from './types'

interface UseGhostBlockOptions {
  defaultDurations?: DefaultDurations
  dayIso?: string
}

export function useGhostBlock({ defaultDurations, dayIso }: UseGhostBlockOptions) {
  const ghost = useDispositionDraftStore((s) => s.ghost)
  const setGhost = useDispositionDraftStore((s) => s.setGhost)
  const commitGhost = useDispositionDraftStore((s) => s.commitGhost)
  const clearGhost = useDispositionDraftStore((s) => s.clearGhost)
  const durations = useDispositionDraftStore((s) => s.durations)
  const setDurations = useDispositionDraftStore((s) => s.setDurations)
  const resetDurations = useDispositionDraftStore((s) => s.resetDurations)
  const updatePosition = useDispositionDraftStore((s) => s.updatePosition)

  const resizeSnapshotRef = useRef<{
    durations: DefaultDurations
    anfahrtStartIso: string
  } | null>(null)
  const resizeHandleRef = useRef<ResizeHandle | null>(null)

  useEffect(() => {
    if (defaultDurations) {
      resetDurations(defaultDurations)
    }
  }, [defaultDurations, resetDurations])

  // Sync ghost date when the timeline day changes (arrow navigation or week→day view switch)
  useEffect(() => {
    if (!dayIso) return

    const currentGhost = useDispositionDraftStore.getState().ghost
    if (!currentGhost?.committed) return

    const ghostDate = new Date(currentGhost.anfahrtStartIso)
    const targetDay = new Date(dayIso)

    const sameDay =
      ghostDate.getFullYear() === targetDay.getFullYear() &&
      ghostDate.getMonth() === targetDay.getMonth() &&
      ghostDate.getDate() === targetDay.getDate()

    if (sameDay) return

    const updated = new Date(
      targetDay.getFullYear(), targetDay.getMonth(), targetDay.getDate(),
      ghostDate.getHours(), ghostDate.getMinutes(), ghostDate.getSeconds(),
    ).toISOString()

    setGhost({ ...currentGhost, anfahrtStartIso: updated })
    updatePosition(updated)
  }, [dayIso, setGhost, updatePosition])

  const totalDraftDuration =
    durations.plannedTravelDuration +
    durations.plannedWorkDuration +
    durations.plannedDepartureDuration

  return {
    ghost,
    setGhost,
    commitGhost,
    clearGhost,
    draftDurations: durations,
    setDraftDurations: setDurations,
    totalDraftDuration,
    resizeSnapshotRef,
    resizeHandleRef,
  }
}
